import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse, PaginatedResponse } from '../types';

// Create axios instance
const createApiInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  // Request interceptor to add auth token
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle token refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Use cookie-based refresh: server reads refresh token from HttpOnly cookie
          const response = await instance.post('/auth/refresh');
          const { token } = response.data.data || {};

          if (token) {
            localStorage.setItem('token', token);
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, logout user
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// API instance
export const api = createApiInstance(
  process.env.REACT_APP_API_URL || 'http://localhost:8000/api'
);

// API service classes
export class AuthService {
  static async login(email: string, password: string) {
    console.log('AuthService.login called with:', { email, password });
    try {
      const res = await api.post('/auth/login', { email, password });
      console.log('AuthService.login response:', res);
      const token = res?.data?.data?.token;
      if (token) localStorage.setItem('token', token);
      return res;
    } catch (error) {
      console.error('AuthService.login error:', error);
      throw error;
    }
  }

  static async register(userData: any) {
    console.log('AuthService.register called with:', userData);
    try {
      const res = await api.post('/auth/register', userData);
      console.log('AuthService.register response:', res);
      const token = res?.data?.data?.token;
      if (token) localStorage.setItem('token', token);
      return res;
    } catch (error) {
      console.error('AuthService.register error:', error);
      throw error;
    }
  }

  static async logout() {
    return api.post('/auth/logout');
  }

  static async refreshToken(refreshToken: string) {
    return api.post('/auth/refresh', { refreshToken });
  }

  static async forgotPassword(email: string) {
    return api.post('/auth/forgot-password', { email });
  }

  static async resetPassword(token: string, password: string) {
    return api.post('/auth/reset-password', { token, password });
  }

  static async verifyEmail(token: string) {
    return api.post('/auth/verify-email', { token });
  }

  static async verifyPhone(code: string) {
    return api.post('/auth/verify-phone', { code });
  }
}

export class UserService {
  static async getCurrentUser() {
    return api.get('/auth/me');
  }

  static async updateUser(userId: string, userData: any) {
    return api.put(`/auth/users/${userId}`, userData);
  }

  static async updateProfile(userData: any) {
    return api.put('/auth/profile', userData);
  }

  static async uploadAvatar(file: File) {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/auth/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static async getPortfolio() {
    return api.get('/auth/portfolio');
  }

  static async addPortfolioImage(file: File, title?: string, description?: string) {
    const formData = new FormData();
    formData.append('image', file);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    return api.post('/auth/portfolio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static async deletePortfolioImage(imageId: string) {
    return api.delete(`/auth/portfolio/${imageId}`);
  }

  static async getUserStats() {
    return api.get('/auth/stats');
  }
}

export class JobService {
  static async getJobsFeed(params?: any) {
    return api.get('/jobs/feed', { params });
  }

  static async searchJobs(params?: any) {
    return api.get('/jobs/search', { params });
  }

  static async getJob(jobId: string) {
    return api.get(`/jobs/${jobId}`);
  }

  static async createJob(jobData: any) {
    return api.post('/jobs', jobData);
  }

  static async updateJob(jobId: string, jobData: any) {
    return api.put(`/jobs/${jobId}`, jobData);
  }

  static async deleteJob(jobId: string) {
    return api.delete(`/jobs/${jobId}`);
  }

  static async getMyPostedJobs(params?: any) {
    return api.get('/jobs/my/posted', { params });
  }

  static async getMyAppliedJobs(params?: any) {
    return api.get('/jobs/my/applied', { params });
  }

  static async uploadJobImages(jobId: string, files: File[]) {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`images`, file);
    });
    return api.post(`/jobs/${jobId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

export class MatchService {
  static async swipeOnJob(jobId: string, direction: 'LEFT' | 'RIGHT') {
    return api.post('/matches/swipe', { jobId, direction });
  }

  static async getMatches(params?: any) {
    return api.get('/matches', { params });
  }

  static async getMatch(matchId: string) {
    return api.get(`/matches/${matchId}`);
  }

  static async respondToInterest(matchId: string, response: 'ACCEPT' | 'DECLINE') {
    return api.post(`/matches/${matchId}/respond`, { response });
  }

  static async getInterestedTradespeople(jobId: string) {
    return api.get(`/matches/job/${jobId}/interested`);
  }
}

export class MessageService {
  static async getConversations(params?: any) {
    return api.get('/messages', { params });
  }

  static async getMatchMessages(matchId: string, params?: any) {
    return api.get(`/messages/match/${matchId}`, { params });
  }

  static async sendMessage(matchId: string, content: string, type: string = 'TEXT') {
    return api.post(`/messages/match/${matchId}`, { content, type });
  }

  static async markMessagesAsRead(matchId: string) {
    return api.put(`/messages/match/${matchId}/read`);
  }

  static async sendQuote(matchId: string, quoteData: any) {
    return api.post(`/messages/match/${matchId}/quote`, quoteData);
  }

  static async uploadFile(matchId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/messages/match/${matchId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

export class SubscriptionService {
  static async getCurrentSubscription() {
    return api.get('/subscriptions/current');
  }

  static async getSubscriptionPlans() {
    return api.get('/subscriptions/plans');
  }

  static async createCheckoutSession(planId: string) {
    return api.post('/subscriptions/checkout', { planId });
  }

  static async changeSubscription(newPlanId: string) {
    return api.post('/subscriptions/change', { planId: newPlanId });
  }

  static async cancelSubscription() {
    return api.post('/subscriptions/cancel');
  }

  static async purchaseBoost(boostType: string) {
    return api.post('/subscriptions/boost', { type: boostType });
  }

  static async getUsageStats() {
    return api.get('/subscriptions/usage');
  }
}

// Utility functions for API calls
export const handleApiError = (error: any): string => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const uploadFile = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const config: AxiosRequestConfig = {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentage);
      }
    },
  };

  const response = await api.post('/upload', formData, config);
  return response.data.data.url;
};

// Default export for backward compatibility
export default api;
